import { useEffect, useMemo, useState } from "react";
import { LineConfig } from "../../../components/Charts";
import chartDataFetcher, { type P2RawDataPoint } from "../../../services/api/ChartDataFetch";
import {
  CHART_DIMENSION_PALETTE,
  CHART_SISTEMA_CAT,
  CHART_SISTEMA_EU,
  chartDistinctSeriesColorAt,
  chartDimensionColorAt,
} from "../../../constants/chartColors";

export type P2Sistema = "EU" | "Catalunya";

export interface P2FilterValues {
  yearRange?: [number, number];
  sectorsSistema: P2Sistema;
  mapaSistema: P2Sistema;
  ambit: string;
}

export interface P2ChartPoint {
  any: string;
  [key: string]: string | number;
}

const AMBIT_COLORS: Record<string, string> = {
  Agricultura: CHART_DIMENSION_PALETTE[0],
  Construcció: CHART_DIMENSION_PALETTE[1],
  Indústria: CHART_DIMENSION_PALETTE[2],
  Serveis: CHART_DIMENSION_PALETTE[3],
};

const SISTEMA_COLORS: Partial<Record<P2Sistema, string>> = {
  Catalunya: CHART_SISTEMA_CAT,
  EU:        CHART_SISTEMA_EU,
};

const SISTEMA_LABELS: Partial<Record<P2Sistema, string>> = {
  Catalunya: "Catalunya",
  EU:        "Europa",
};

const GENERAL_SECTOR_AMBITS = new Set(["Agricultura", "Construcció", "Indústria", "Serveis"]);

const isGeneralSectorAmbit = (ambit: string) => GENERAL_SECTOR_AMBITS.has(ambit);
const isServicesSubsectorAmbit = (ambit: string) => !GENERAL_SECTOR_AMBITS.has(ambit);

function pivotByAmbit(filtered: P2RawDataPoint[]): P2ChartPoint[] {
  const anys = Array.from(new Set(filtered.map((d) => d.any))).sort();

  return anys.map((any) => {
    const point: P2ChartPoint = { any: String(any) };
    filtered
      .filter((d) => d.any === any)
      .forEach((d) => { point[d.ambit] = d.ratio; });
    return point;
  });
}

function buildAmbitLines(filtered: P2RawDataPoint[]): LineConfig[] {
  const ambits = Array.from(new Set(filtered.map((d) => d.ambit))).sort();

  let subsectorIndex = 0;

  return ambits.map((ambit, i) => {
    const isMacro = isGeneralSectorAmbit(ambit);
    const color = AMBIT_COLORS[ambit]
      ?? (isMacro ? chartDimensionColorAt(i) : chartDistinctSeriesColorAt(subsectorIndex++));

    return {
      key:   ambit,
      label: ambit,
      color,
    };
  });
}

function pivotBySistema(filtered: P2RawDataPoint[]): P2ChartPoint[] {
  const anys = Array.from(new Set(filtered.map((d) => d.any))).sort();

  return anys.map((any) => {
    const point: P2ChartPoint = { any: String(any) };
    filtered
      .filter((d) => d.any === any)
      .forEach((d) => {
        const label = SISTEMA_LABELS[d.sistema as P2Sistema] ?? d.sistema;
        point[label] = d.ratio;
      });
    return point;
  });
}

function buildSistemaLines(filtered: P2RawDataPoint[]): LineConfig[] {
  const sistemes = Array.from(
    new Set(filtered.map((d) => d.sistema as P2Sistema))
  ).sort() as P2Sistema[];

  return sistemes.map((s, i) => ({
    key:   SISTEMA_LABELS[s] ?? s,
    label: SISTEMA_LABELS[s] ?? s,
    color: SISTEMA_COLORS[s] ?? chartDimensionColorAt(i),
  }));
}

export function useP2Data(filters: P2FilterValues) {
  const [rawData, setRawData] = useState<P2RawDataPoint[]>(
    () => chartDataFetcher.getCachedSync<P2RawDataPoint[]>("p2") ?? []
  );
  const [loading, setLoading] = useState(
    () => chartDataFetcher.getCachedSync<P2RawDataPoint[]>("p2") === null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (chartDataFetcher.getCachedSync("p2") !== null) return;

    let cancelled = false;

    setLoading(true);
    setError(null);

    chartDataFetcher
      .getP2Data()
      .then((data) => { if (!cancelled) setRawData(data); })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Error carregant dades");
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, []);

  const availableAmbits = useMemo(() => {
    return Array.from(new Set(rawData.map((d) => d.ambit)))
      .filter(isGeneralSectorAmbit)
      .sort();
  }, [rawData]);

  const yearFiltered = useMemo(() => {
    if (!filters.yearRange) return rawData;
    const [min, max] = filters.yearRange;
    return rawData.filter((d) => d.any >= min && d.any <= max);
  }, [rawData, filters.yearRange]);

  const { chart1Data, chart1Lines, chart1SubsectorData, chart1SubsectorLines } = useMemo(() => {
    const sectorFiltered = yearFiltered.filter(
      (d) => d.sistema === filters.sectorsSistema && isGeneralSectorAmbit(d.ambit)
    );
    const subsectorFiltered = yearFiltered.filter(
      (d) => d.sistema === filters.sectorsSistema && isServicesSubsectorAmbit(d.ambit)
    );

    return {
      chart1Data: pivotByAmbit(sectorFiltered),
      chart1Lines: buildAmbitLines(sectorFiltered),
      chart1SubsectorData: pivotByAmbit(subsectorFiltered),
      chart1SubsectorLines: buildAmbitLines(subsectorFiltered),
    };
  }, [yearFiltered, filters.sectorsSistema]);

  const { chart1MapData, chart1MapLines } = useMemo(() => {
    const filtered = yearFiltered.filter(
      (d) => d.sistema === filters.mapaSistema && isGeneralSectorAmbit(d.ambit)
    );
    return {
      chart1MapData: pivotByAmbit(filtered),
      chart1MapLines: buildAmbitLines(filtered),
    };
  }, [yearFiltered, filters.mapaSistema]);

  const { chart2Data, chart2Lines } = useMemo(() => {
    const filtered = yearFiltered.filter(
      (d) => d.ambit === filters.ambit && isGeneralSectorAmbit(d.ambit)
    );
    return {
      chart2Data: pivotBySistema(filtered),
      chart2Lines: buildSistemaLines(filtered),
    };
  }, [yearFiltered, filters.ambit]);

  return {
    chart1Data,
    chart1Lines,
    chart1SubsectorData,
    chart1SubsectorLines,
    chart1MapData,
    chart1MapLines,
    chart2Data,
    chart2Lines,
    availableAmbits,
    loading,
    error,
  };
}
